import type { Conversation, ConversationMessage } from "@/lib/conversationAPI";
import {
  STORAGE_KEY,
  readLocalConversations,
  writeLocalConversations,
} from "@/lib/conversationAPI";
import { guestVoiceAnalyticsHeaders } from "@/lib/experiments/guestVoiceRuntimeClient";
import { supabase } from "@/lib/supabaseClient";

export const PENDING_GUEST_SESSION_CLAIM_KEY = "chicky-pending-guest-session-claim-v1";
export const GUEST_SESSION_CLAIM_EVENT = "chicky:guest-session-claimed";

const CLAIM_SCHEMA_VERSION = 2 as const;
const CLAIM_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1_000;
const SIGNUP_WINDOW_MS = 2 * 60 * 60 * 1_000;
const MAX_TRANSCRIPT_MESSAGES = 40;
let activeGuestSessionClaim: Promise<GuestSessionClaimResult> | null = null;

export type PendingGuestSessionClaim = {
  schemaVersion: typeof CLAIM_SCHEMA_VERSION;
  claimId: string;
  stagedAt: number;
  localConversationId: string;
  title: string;
  languageName: string;
  languageLevel: Conversation["languageLevel"];
  createdAt: number;
  updatedAt: number;
  messages: ConversationMessage[];
};

export type GuestSessionClaimResult = {
  claimed: true;
  conversationId: string;
  sessionReportId: string;
  alreadyClaimed: boolean;
  summaryReady: boolean;
  summaryStatus: "succeeded" | "skipped" | "failed" | "pending" | "already_succeeded";
};

function createClaimId(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function normalizeClaimMessages(value: unknown): ConversationMessage[] {
  if (!Array.isArray(value)) return [];

  const normalized: ConversationMessage[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") continue;
    const message = candidate as Partial<ConversationMessage>;
    if (message.role !== "user" && message.role !== "assistant") continue;
    if (typeof message.content !== "string" || typeof message.timestamp !== "number") continue;
    const content = message.content.trim();
    if (!content || !Number.isFinite(message.timestamp)) continue;

    const previous = normalized.at(-1);
    if (previous?.role === message.role && previous.content === content) continue;
    normalized.push({ role: message.role, content, timestamp: message.timestamp });
  }

  return normalized.slice(-MAX_TRANSCRIPT_MESSAGES);
}

function parsePendingGuestSessionClaim(value: unknown): PendingGuestSessionClaim | null {
  if (!value || typeof value !== "object") return null;
  const claim = value as Record<string, unknown>;
  const messages = normalizeClaimMessages(
    claim.schemaVersion === 1 ? claim.tutorMessages : claim.messages
  );
  const validLevels = new Set<Conversation["languageLevel"]>([
    "absolute_beginner",
    "beginner",
    "intermediate",
    "advanced",
  ]);
  const isValid =
    (claim.schemaVersion === 1 || claim.schemaVersion === CLAIM_SCHEMA_VERSION) &&
    typeof claim.claimId === "string" &&
    typeof claim.stagedAt === "number" &&
    typeof claim.localConversationId === "string" &&
    typeof claim.title === "string" &&
    typeof claim.languageName === "string" &&
    typeof claim.languageLevel === "string" &&
    validLevels.has(claim.languageLevel as Conversation["languageLevel"]) &&
    typeof claim.createdAt === "number" &&
    typeof claim.updatedAt === "number" &&
    messages.some((message) => message.role === "assistant");
  if (!isValid) return null;

  return {
    schemaVersion: CLAIM_SCHEMA_VERSION,
    claimId: claim.claimId as string,
    stagedAt: claim.stagedAt as number,
    localConversationId: claim.localConversationId as string,
    title: claim.title as string,
    languageName: claim.languageName as string,
    languageLevel: claim.languageLevel as Conversation["languageLevel"],
    createdAt: claim.createdAt as number,
    updatedAt: claim.updatedAt as number,
    messages,
  };
}

export function readPendingGuestSessionClaim(
  storage: Pick<Storage, "getItem" | "removeItem"> = localStorage
): PendingGuestSessionClaim | null {
  try {
    const raw = storage.getItem(PENDING_GUEST_SESSION_CLAIM_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const pending = parsePendingGuestSessionClaim(parsed);
    if (!pending || Date.now() - pending.stagedAt > CLAIM_MAX_AGE_MS) {
      storage.removeItem(PENDING_GUEST_SESSION_CLAIM_KEY);
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

export function stageLatestGuestSpeakingSessionForClaim(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = localStorage
): PendingGuestSessionClaim | null {
  const existing = readPendingGuestSessionClaim(storage);

  let conversations: Conversation[] = [];
  try {
    const raw = storage.getItem(STORAGE_KEY);
    conversations = raw ? (JSON.parse(raw) as Conversation[]) : [];
  } catch {
    storage.removeItem(PENDING_GUEST_SESSION_CLAIM_KEY);
    return null;
  }

  const source = conversations
    .filter((conversation) => conversation?.conversationKind === "speaking")
    .sort((left, right) => right.updatedAt - left.updatedAt)
    .find((conversation) =>
      conversation.messages.some(
        (message) => message.role === "assistant" && message.content.trim().length > 0
      )
    );
  if (!source) {
    storage.removeItem(PENDING_GUEST_SESSION_CLAIM_KEY);
    return null;
  }

  if (existing?.localConversationId === source.id && existing.updatedAt === source.updatedAt) {
    return existing;
  }

  const messages = normalizeClaimMessages(source.messages);
  if (!messages.some((message) => message.role === "assistant")) {
    storage.removeItem(PENDING_GUEST_SESSION_CLAIM_KEY);
    return null;
  }

  const pending: PendingGuestSessionClaim = {
    schemaVersion: CLAIM_SCHEMA_VERSION,
    claimId: createClaimId(),
    stagedAt: Date.now(),
    localConversationId: source.id,
    title: source.title,
    languageName: source.languageName,
    languageLevel: source.languageLevel,
    createdAt: source.createdAt,
    updatedAt: source.updatedAt,
    messages,
  };
  storage.setItem(PENDING_GUEST_SESSION_CLAIM_KEY, JSON.stringify(pending));
  return pending;
}

export function isNewAccountForGuestClaim(
  userCreatedAt: string | null | undefined,
  stagedAt: number
): boolean {
  const createdAt = Date.parse(userCreatedAt || "");
  if (!Number.isFinite(createdAt)) return false;
  return Math.abs(createdAt - stagedAt) <= SIGNUP_WINDOW_MS;
}

export async function claimGuestSession(
  pending: PendingGuestSessionClaim
): Promise<GuestSessionClaimResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error("Authenticated session is unavailable");

  const response = await fetch("/api/conversations/claim-guest", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      ...guestVoiceAnalyticsHeaders(),
    },
    body: JSON.stringify(pending),
  });
  if (!response.ok) {
    throw new Error((await response.text()) || `Guest session claim failed (${response.status})`);
  }
  return response.json() as Promise<GuestSessionClaimResult>;
}

export function registerActiveGuestSessionClaim(
  claim: Promise<GuestSessionClaimResult>
): Promise<GuestSessionClaimResult> {
  activeGuestSessionClaim = claim;
  const clearIfCurrent = () => {
    if (activeGuestSessionClaim === claim) activeGuestSessionClaim = null;
  };
  void claim.then(clearIfCurrent, clearIfCurrent);
  return claim;
}

export async function waitForActiveGuestSessionClaim(timeoutMs = 4_000): Promise<void> {
  const claim = activeGuestSessionClaim;
  if (!claim) return;
  await Promise.race([
    claim.then(() => undefined).catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

export function removeClaimedLocalConversation(localConversationId: string): void {
  const conversations = readLocalConversations();
  writeLocalConversations(
    conversations.filter((conversation) => conversation.id !== localConversationId)
  );
}

export function clearPendingGuestSessionClaim(): void {
  try {
    localStorage.removeItem(PENDING_GUEST_SESSION_CLAIM_KEY);
  } catch {}
}
